/**
 * Unit Conversion Utilities
 * Provides consistent unit conversion across all trip components
 */

// Speed conversions (from knots)
export const convertSpeed = (knots, speedUnit = 'knots') => {
  if (!knots || isNaN(knots)) return 0;
  
  switch (speedUnit) {
    case 'mph':
      return knots * 1.15078; // knots to mph
    case 'kmh':
      return knots * 1.852; // knots to km/h
    default:
      return knots; // knots
  }
};

export const getSpeedUnit = (speedUnit = 'knots') => {
  switch (speedUnit) {
    case 'mph':
      return 'mph';
    case 'kmh':
      return 'km/h';
    default:
      return 'kts';
  }
};

// Distance conversions (from nautical miles)
export const convertDistance = (nauticalMiles, distanceUnit = 'nautical_miles') => {
  if (!nauticalMiles || isNaN(nauticalMiles)) return 0;
  
  switch (distanceUnit) {
    case 'kilometers':
      return nauticalMiles * 1.852; // nm to km
    case 'statute_miles':
      return nauticalMiles * 1.15078; // nm to statute miles
    default:
      return nauticalMiles; // nautical miles
  }
};

export const getDistanceUnit = (distanceUnit = 'nautical_miles') => {
  switch (distanceUnit) {
    case 'kilometers':
      return 'km';
    case 'statute_miles':
      return 'mi';
    default:
      return 'nm';
  }
};

// Temperature conversions (from Celsius)
export const convertTemperature = (celsius, tempUnit = 'celsius') => {
  if (!celsius || isNaN(celsius)) return 0;
  
  if (tempUnit === 'fahrenheit') {
    return (celsius * 9/5) + 32; // celsius to fahrenheit
  }
  return celsius; // celsius
};

export const getTemperatureUnit = (tempUnit = 'celsius') => {
  return tempUnit === 'fahrenheit' ? '°F' : '°C';
};

// Fuel conversions (from liters)
export const convertFuel = (liters, fuelUnit = 'liters') => {
  if (!liters || isNaN(liters)) return 0;
  
  switch (fuelUnit) {
    case 'gallons':
      return liters * 0.264172; // liters to US gallons
    case 'imperial_gallons':
      return liters * 0.219969; // liters to imperial gallons
    default:
      return liters; // liters
  }
};

export const getFuelUnit = (fuelUnit = 'liters') => {
  switch (fuelUnit) {
    case 'gallons':
      return 'gal';
    case 'imperial_gallons':
      return 'imp gal';
    default:
      return 'L';
  }
};

/** Normalize SignalK m³/s or stored L/hr to liters per hour. */
export const fuelRateToLitersPerHour = (raw) => {
  const n = parseFloat(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n >= 0.5 && n <= 500) return n;
  if (n < 0.5) return n * 3600000;
  return n;
};

export const formatFuelRate = (litersPerHour, fuelUnit = 'liters', decimals = 2) => {
  const lph = fuelRateToLitersPerHour(litersPerHour);
  if (!lph) return '—';
  return `${convertFuel(lph, fuelUnit).toFixed(decimals)} ${getFuelUnit(fuelUnit)}/hr`;
};

export const formatCombinedFuelRate = (portRaw, starboardRaw, fuelUnit = 'liters', decimals = 2) => {
  const totalLph =
    fuelRateToLitersPerHour(portRaw) + fuelRateToLitersPerHour(starboardRaw);
  if (!totalLph) return '—';
  return `${convertFuel(totalLph, fuelUnit).toFixed(decimals)} ${getFuelUnit(fuelUnit)}/hr`;
};

/** Accept API field names from trip_user_preferences (singular) and legacy plural keys. */
export const resolveTripPreferenceUnits = (prefs = {}) => ({
  speed_unit: prefs.speed_unit || prefs.speed_units || 'knots',
  distance_unit: prefs.distance_unit || prefs.distance_units || 'nautical_miles',
  temperature_unit: prefs.temperature_unit || prefs.temperature_units || 'celsius',
  fuel_unit: prefs.fuel_unit || prefs.fuel_units || 'liters',
});

// Duration formatting
export const formatDuration = (minutes) => {
  if (!minutes || isNaN(minutes)) return 'N/A';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

// Format speed with unit
export const formatSpeed = (knots, speedUnit = 'knots', decimals = 1) => {
  const numKnots = parseFloat(knots);
  if (isNaN(numKnots) || numKnots === null || numKnots === undefined) return `0.${'0'.repeat(decimals)} ${getSpeedUnit(speedUnit)}`;
  const converted = convertSpeed(numKnots, speedUnit);
  if (isNaN(converted) || typeof converted !== 'number') return `0.${'0'.repeat(decimals)} ${getSpeedUnit(speedUnit)}`;
  const unit = getSpeedUnit(speedUnit);
  return `${Number(converted).toFixed(decimals)} ${unit}`;
};

// Format distance with unit
export const formatDistance = (nauticalMiles, distanceUnit = 'nautical_miles', decimals = 2) => {
  const numNauticalMiles = parseFloat(nauticalMiles);
  if (isNaN(numNauticalMiles) || numNauticalMiles === null || numNauticalMiles === undefined) return `0.${'0'.repeat(decimals)} ${getDistanceUnit(distanceUnit)}`;
  const converted = convertDistance(numNauticalMiles, distanceUnit);
  if (isNaN(converted) || typeof converted !== 'number') return `0.${'0'.repeat(decimals)} ${getDistanceUnit(distanceUnit)}`;
  const unit = getDistanceUnit(distanceUnit);
  return `${Number(converted).toFixed(decimals)} ${unit}`;
};

// Format temperature with unit
export const formatTemperature = (celsius, tempUnit = 'celsius', decimals = 0) => {
  const numCelsius = parseFloat(celsius);
  if (isNaN(numCelsius) || numCelsius === null || numCelsius === undefined) return `0${'0'.repeat(decimals)}${getTemperatureUnit(tempUnit)}`;
  const converted = convertTemperature(numCelsius, tempUnit);
  if (isNaN(converted) || typeof converted !== 'number') return `0${'0'.repeat(decimals)}${getTemperatureUnit(tempUnit)}`;
  const unit = getTemperatureUnit(tempUnit);
  return `${Number(converted).toFixed(decimals)}${unit}`;
};

// Format fuel with unit
export const formatFuel = (liters, fuelUnit = 'liters', decimals = 1) => {
  const numLiters = parseFloat(liters);
  if (isNaN(numLiters) || numLiters === null || numLiters === undefined) return `0.${'0'.repeat(decimals)} ${getFuelUnit(fuelUnit)}`;
  const converted = convertFuel(numLiters, fuelUnit);
  if (isNaN(converted) || typeof converted !== 'number') return `0.${'0'.repeat(decimals)} ${getFuelUnit(fuelUnit)}`;
  const unit = getFuelUnit(fuelUnit);
  return `${Number(converted).toFixed(decimals)} ${unit}`;
};
